import { useToastr } from '@farmshare/ui-components';
import { Button, Chip, Stack } from '@mui/material';
import { type MouseEvent, useState } from 'react';

import {
  type NotificationPromptState,
  useNotificationPromptAnswerMutation,
} from '../../lib/graphql';

type PromptState = Pick<
  NotificationPromptState,
  '_id' | 'kind' | 'status' | 'canAnswer'
>;

interface NotificationPromptActionsProps {
  /** The question the notification asked, from the bell item or chat bubble. */
  prompt: PromptState;
  /** Where the buttons are shown — recorded with the answer. */
  via: 'in_app' | 'chat';
}

/** Button and status copy per prompt kind. */
const PROMPT_COPY: Record<
  string,
  { yes: string; no: string; answeredYes: string; answeredNo: string }
> = {
  dropoff_confirmation: {
    yes: 'Confirm drop-off',
    no: "Can't make it",
    answeredYes: 'Drop-off confirmed',
    answeredNo: "Can't make it",
  },
  wholesale_order_confirmation: {
    yes: 'Confirm order',
    no: 'Skip this one',
    answeredYes: 'Order confirmed',
    answeredNo: 'Skipped',
  },
  // The order assistant's summary: YES sends it to the plant to accept.
  order_agent_summary: {
    yes: 'Send it',
    no: 'Change something',
    answeredYes: 'Sent to the plant',
    answeredNo: 'Changing it',
  },
};
const DEFAULT_COPY = {
  yes: 'Yes',
  no: 'No',
  answeredYes: 'Answered yes',
  answeredNo: 'Answered no',
};

/**
 * The answer buttons for a confirmable notification ("Reply Y to confirm or N
 * if you can't make it"), shown under its bell item and its chat bubble.
 *
 * The customer who was asked gets Confirm / Can't make it while the question
 * is open; once it's answered — here, by text or from the email — everyone sees
 * the answer instead. Processor staff reading the same thread see "Awaiting
 * confirmation" in place of the buttons. A lapsed question shows nothing.
 * @param props
 * @param props.prompt
 * @param props.via
 */
export function NotificationPromptActions({
  prompt,
  via,
}: NotificationPromptActionsProps) {
  const { push } = useToastr();
  const [answerPrompt, { loading }] = useNotificationPromptAnswerMutation();
  // The chat list keeps its own copy of each message, so the mutation's result
  // is held here rather than read back through the Apollo cache.
  const [answered, setAnswered] = useState<PromptState | null>(null);
  const current = answered ?? prompt;
  const copy = PROMPT_COPY[current.kind] ?? DEFAULT_COPY;

  const handleAnswer =
    (answer: 'yes' | 'no') => async (e: MouseEvent<HTMLElement>) => {
      // The bell item behind the buttons navigates on click.
      e.stopPropagation();
      try {
        const { data } = await answerPrompt({
          variables: { promptId: current._id, answer, via },
        });
        if (data?.notificationPromptAnswer) {
          setAnswered(data.notificationPromptAnswer);
        }
      } catch {
        push({
          title: 'Answer not saved',
          body: "We couldn't save your answer. Please try again.",
          severity: 'error',
        });
      }
    };

  if (current.status === 'answered_yes') {
    return <Chip size="small" color="success" label={copy.answeredYes} />;
  }
  if (current.status === 'answered_no') {
    return <Chip size="small" color="warning" label={copy.answeredNo} />;
  }
  if (current.status !== 'pending') return null;
  if (!current.canAnswer) {
    return (
      <Chip size="small" variant="outlined" label="Awaiting confirmation" />
    );
  }

  return (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        variant="contained"
        disabled={loading}
        onClick={handleAnswer('yes')}
      >
        {copy.yes}
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        disabled={loading}
        onClick={handleAnswer('no')}
      >
        {copy.no}
      </Button>
    </Stack>
  );
}
